/**
 * hooks/useFloatingPanel.js -- Motor de posicionamiento de paneles
 * flotantes sobre Floating UI (@floating-ui/react), migrado desde una
 * implementacion casera (2026-08-29 -- ver historial de este archivo si
 * hace falta el `computeFloatingPosition` original). Floating UI resuelve
 * de fabrica lo que antes eran parches manuales:
 *
 *   - flip + shift: voltea arriba y recorta contra los 4 bordes de la
 *     pantalla (la version casera solo volteaba arriba y recortaba
 *     izquierda/derecha, nunca top/bottom en combinacion).
 *   - size: iguala el ancho del panel al del trigger (matchTriggerWidth)
 *     sin el bug de "medir el ancho ANTES de aplicar el override" que
 *     tuvimos que parchar a mano (bug 2026-08-28: el combo saltaba a la
 *     izquierda en su primer open).
 *   - autoUpdate: reposiciona en scroll/resize Y cuando el PROPIO PANEL
 *     cambia de tamano via ResizeObserver -- algo que la version casera
 *     nunca cubrio (un combo que se filtra y cambia de alto se quedaba
 *     con la posicion vieja).
 *
 * Se conserva la MISMA forma de retorno que la version casera
 * ({ open, setOpen, pos, triggerRef, panelRef }) a proposito: los
 * consumidores existentes (SingleSelectCombo, MultiSelectFilter,
 * SearchableTextCombo, DatePicker, TimePicker, RowActionsMenu,
 * ProfileMenu) siguen haciendo `ref={triggerRef}` /
 * `pos?.left` / `pos?.top` / `pos?.width` / `pos?.zIndex` sin cambiar una
 * linea -- React acepta tanto un ref-object como un callback-ref en la
 * prop `ref`, y `refs.setReference`/`refs.setFloating` de Floating UI son
 * callback-refs validos ahi. Se suma `context` en el retorno SOLO para
 * quien quiera navegacion por teclado encima (ver useComboListNav.js);
 * quien no lo use, lo ignora sin costo.
 *
 * A proposito NO se adopta useDismiss/useClick de Floating UI en este
 * pase: cada consumidor ya decide COMO se abre su panel (algunos con
 * logica extra, como limpiar el termino de busqueda al abrir) -- cambiar
 * ademas el modelo de interacciones hubiera sido un cambio mas grande de
 * lo pedido, y no se puede validar sin Node/navegador real en esta
 * maquina. El cierre por click-afuera y Escape se queda casero.
 */
import { useCallback, useEffect, useState } from 'react';
import { useFloating, autoUpdate, offset, flip, shift, size } from '@floating-ui/react';

const GAP = 4;

// El límite se aplica solo al scroll interior; el marco aporta sus bordes.
// No imponer una altura mínima que pueda exceder el espacio disponible.
const PANEL_SCROLL_MAX_PX = 272;

export function useFloatingPanel({ matchTriggerWidth = false, allowOverlap = false, deps = [] } = {}) {
  const [open, setOpen] = useState(false);
  const [matchedWidth, setMatchedWidth] = useState(undefined);
  const [availableHeight, setAvailableHeight] = useState(undefined);

  const middleware = [offset(GAP), flip({ padding: GAP }), shift({ padding: GAP, crossAxis: allowOverlap })];
  middleware.push(size({
    padding: GAP,
    apply({ rects, elements, availableHeight: h }) {
      const style = getComputedStyle(elements.floating);
      const frameHeight = ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom']
        .reduce((total, key) => total + (parseFloat(style[key]) || 0), 0);
      setAvailableHeight(Math.max(0, h - frameHeight));
      if (matchTriggerWidth) setMatchedWidth(rects.reference.width);
    },
  }));

  const { refs, x, y, context, update } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware,
    whileElementsMounted: autoUpdate,
  });

  // `deps` (p. ej. el mes visible del datepicker) cambia el CONTENIDO del
  // panel sin que `open` cambie de valor -- autoUpdate ya cubre cambios de
  // tamano via ResizeObserver, pero se fuerza un update explicito aqui
  // para no depender de que el observer dispare a tiempo en todos los
  // navegadores.
  useEffect(() => {
    if (open) update?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, update, ...deps]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (refs.reference.current?.contains?.(e.target) || refs.floating.current?.contains?.(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pos = open ? {
    left: x ?? -9999,
    top: y ?? -9999,
    width: matchTriggerWidth ? matchedWidth : undefined,
    // Panel a lo mas 17rem (mismo tope visual de siempre), pero NUNCA mas
    // que el espacio que Floating UI ya midio disponible en ese lado --
    // ver PANEL_SCROLL_MAX_PX arriba. `panelRef`/CSS le ponen scroll
    // interno, asi que encoger el alto nunca esconde opciones: solo hace
    // que quepan todas con scroll en vez de desbordar la pantalla.
    // El lanzador de módulos usa el espacio real, sin el tope de los combos.
    availableHeight,
    scrollMaxHeight: availableHeight != null ? Math.min(availableHeight, PANEL_SCROLL_MAX_PX) : undefined,
    // Portal a document.body saca el panel de .modal-overlay: deja de
    // estar "contenido" por su z-index y tiene que competir directo
    // contra --z-modal. --z-floating ya es mayor que --z-modal en tokens.css.
    zIndex: 'var(--z-floating)',
  } : null;

  // Keep body portals inside their owning dialog's keyboard scope.
  const panelRef = useCallback((node) => {
    if (!node && refs.floating.current?.contains(document.activeElement)) {
      refs.reference.current?.focus({ preventScroll: true });
    }
    refs.setFloating(node);
    const owner = refs.reference.current?.closest('[data-dialog-id]');
    if (node && owner) node.dataset.dialogOwner = owner.dataset.dialogId;
  }, [refs.setFloating, refs.reference, refs.floating]);

  return { open, setOpen, pos, triggerRef: refs.setReference, panelRef, context };
}
